import React, { useState } from "react";
import {
  Typography,
  Select,
  MenuItem,
  Button,
  Paper,
  Box,
  makeStyles,
  Grid,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  TextField,
} from "@material-ui/core";
import DatePickerTDSAT from "features/Tdsat/components/DatePickerTDSAT";
import { getStatementReports } from "features/Tdsat/API/TdsatApi";
import validateDateAndFormat from "utils/validateDateAndFormat";
import Swal from "sweetalert2";

const useStyles = makeStyles((theme) => ({
  form: {
    maxWidth: 800,
    margin: "0 auto",
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    marginBottom: theme.spacing(2.5),
    marginTop: theme.spacing(2.5),
  },
  paper: {
    padding: theme.spacing(4),
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    width: "600",
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(1),
    borderRadius: "10px",
    marginX: 'auto'
  },
  headingsForm: {
    display: "flex",
    paddingBottom: "8px",
    marginTop: "24px",
    marginBottom: "24px",
    borderBottom: "1px solid black",
    color: "#000000",
    fontWeight: "700",
  },
  error: {
    color: "red",
    fontSize: "0.85rem",
    marginTop: theme.spacing(0.5),
  },
}));

function StatementCis() {
  const classes = useStyles();

  const [statementType, setStatementType] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [tillDate, setTillDate] = useState(false);
  const [statementFormat, setStatementFormat] = useState("year wise"); // default year wise
  const [graphWise, setGraphWise] = useState(false);
  const [errors, setErrors] = useState({});

  const renderDropdown = (label, value, onChange, options, required) => (
        <FormControl fullWidth size="small" variant='outlined' required={required}>
            <InputLabel>{label}</InputLabel>
            <Select value={value} onChange={onChange} label={label}>
                {options.map((option) => (
                <MenuItem key={option?.value || option} value={option?.value || option?.label || option}>
                    {option?.label || option}
                </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

  const statementChoices = [
    {
      label: 'Total Cases',
      value: 'total_cases',
    },
    {
      label: 'Dispose Cases',
      value: 'disposed_cases',
    },
    {
      label: 'Pending Cases',
      value: 'pending_cases',
    },
    {
      label: 'Institution Cases',
      value: 'institution_cases',
    },
    {
      label: 'MA in Dispose Case',
      value: 'ma_cases',
    }
  ]

  const validate = () => {
    let temp = {};
    if (!statementType) temp.statementType = 'Statement type is required';
    if (!fromDate) temp.fromDate = "From Date is required";
    if (!toDate && !tillDate)
      temp.toDate = "Either To Date or Till Date is required";
    if (!statementFormat)
      temp.statementFormat = "Statement format is required";
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      const queryParams = {
        start_date: validateDateAndFormat(fromDate, 'dd-MM-yyyy'),
        end_date: tillDate ? validateDateAndFormat(new Date(), 'dd-MM-yyyy') : validateDateAndFormat(toDate, 'dd-MM-yyyy'),
        statement_type: statementChoices?.find(element => element?.value === statementType)?.label,
        format_type: statementFormat,
        graph_wise: graphWise,
      } 
      try {
        const response = await getStatementReports(statementType, queryParams);
        if (response.data) {
          const newWindow = window.open(response?.data?.pre_signed_url, '_blank');
          // Optional: Add a check for pop-up blockers
          if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
            Swal.fire({
              title: "Pop-up Blocked!",
              text: "Please enable pop-ups for this site to view the PDF report.",
              icon: "warning"
            });
          }
        } else {
          Swal.fire({
            title: "Error",
            text: "No PDF data received from the server.",
            icon: "error"
          });
        }
  
      } catch (error) {
          console.error("There was an error fetching the case data!", error);
      }
    }
  };

  return (
    <Box component="form" className={classes.form} onSubmit={handleSubmit}>
      <Typography variant="h4" align="center" className={classes.title}>
        Statement
      </Typography>
      <Paper className={classes.paper}>
        <Grid container spacing={3}>
          {/* Statement Type */}
          <Grid item xs={12}>
            <Typography variant="h6" className={classes.headingsForm}>
              Statement Type
            </Typography>
            {renderDropdown(
              "Select",
              statementType,
              (e) => setStatementType(e.target.value),
              statementChoices,
              true,
            )}
            {errors.statementType && (
              <Typography className={classes.error}>
                {errors.statementType}
              </Typography>
            )}
          </Grid>

          {/* Dates Row */}
          <Grid item xs={12} style={{alignItems:'flex-start'}}>
            <Typography variant="h6" className={classes.headingsForm}>
              Date Range
            </Typography>
            <Grid container spacing={2} alignItems='flex-start'>
              {/* From Date */}
              <Grid item xs={4}>
                <DatePickerTDSAT
                  date={fromDate}
                  isRequired={true}
                  label={'From date'}
                  setDate={(value)=> setFromDate(value)}
                  size="small"
                />
                {errors.fromDate && (
                <Typography className={classes.error}>
                  {errors.fromDate}
                </Typography>
              )}
              </Grid>

              {/* To Date */}
              <Grid item xs={4}>
                <DatePickerTDSAT
                  date={toDate}
                  label={'To date'}
                  setDate={(value)=> setToDate(value)}
                  size="small"
                  disabled={tillDate}
                  isRequired={!tillDate}
                />
                {errors.toDate && (
                  <Typography className={classes.error}>
                    {errors.toDate}
                  </Typography>
                )}
              </Grid>

              {/* Till Date */}
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tillDate}
                      onChange={(e) => {
                        setTillDate(e.target.checked);
                        if (e.target.checked) setToDate(null);
                      }}
                      color="primary"
                    />
                  }
                  label="Till Date"
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Statement Format */}
          <Grid item xs={12}>
            <Typography variant="h6" className={classes.headingsForm}>
              Statement Format
            </Typography>
            <FormControl fullWidth component="fieldset" error={!!errors.statementFormat}>
              <RadioGroup
                row
                value={statementFormat}
                onChange={(e) => setStatementFormat(e.target.value)}
              >
                <FormControlLabel
                  value="year wise"
                  control={<Radio color="primary" />}
                  label="Year Wise"
                />
                <FormControlLabel
                  value="month wise"
                  control={<Radio color="primary" />}
                  label="Month Wise"
                />
              </RadioGroup>
              {errors.statementFormat && (
                <Typography className={classes.error}>
                  {errors.statementFormat}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Advance Search */}
          <Grid item xs={12}>
            <Typography variant="h6" className={classes.headingsForm}>
              Advance Search
            </Typography>
            <FormControlLabel
              style={{width: '100%'}}
              control={
                <Checkbox
                  checked={graphWise}
                  onChange={(e) => setGraphWise(e.target.checked)}
                  color="primary"
                />
              }
              label="Graph Wise"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Submit */}
      <Grid>
        <Button variant="contained" color="primary" type="submit">
          Download Report
        </Button>
      </Grid>
    </Box>
  );
}

export default StatementCis;
